import jwtDecode from 'jwt-decode';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { resetAllApiState, RootState } from 'store';
import { IUser } from 'store/user/interfaces';
import {
  confirmEmailMutation,
  loginMutation,
  resetPasswordMutation,
  changePasswordMutation,
  loginCandidateMutation,
  IRegisterByInvite,
  registerByInviteMutation,
  confirmTokenMutation,
  getSSOLinkMutation,
  unsecuredCandidateLogin,
} from './interfaces';
import { apiService, checkUserTag, organizationTag } from '../api';
import { UserRoleEnum } from '../../common/user/enums/user-role.enum';

const authService = apiService.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<
      loginMutation['response'],
      loginMutation['request']
    >({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/auth/signIn',
          method: 'POST',
          body: {
            ...args,
            email: args.email.trim().toLowerCase(),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as IUser };
      },
      invalidatesTags: [checkUserTag, organizationTag],
    }),
    getSSOUrl: builder.mutation<
      getSSOLinkMutation['response'],
      getSSOLinkMutation['request']
    >({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/auth/sso/link',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as { url: string } };
      },
    }),
    confirmToken: builder.mutation<
      confirmTokenMutation['response'],
      confirmTokenMutation['request']
    >({
      queryFn(args) {
        return { data: args.user as IUser };
      },
      invalidatesTags: [checkUserTag, organizationTag],
    }),
    checkUser: builder.query<IUser | false, void>({
      async queryFn(_args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/user',
          method: 'GET',
        });

        if (result.error) {
          return { data: false };
        }

        const data = (result.data as IUser) ?? {};

        data.avatar = data?.avatar
          ? `${data?.avatar}?${Date.now()}`
          : data?.avatar;

        return { data: data };
      },
      providesTags: [checkUserTag],
    }),
    checkUserCandidate: builder.query<IUser | false, void>({
      async queryFn(_args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/user/candidate',
          method: 'GET',
        });

        if (result.error) {
          return { data: false };
        }

        const data = (result.data as IUser) ?? {};

        data.avatar = data?.avatar
          ? `${data?.avatar}?${Date.now()}`
          : data?.avatar;

        return { data: data };
      },
      providesTags: [checkUserTag],
    }),
    loginCandidate: builder.mutation<
      loginCandidateMutation['response'],
      loginCandidateMutation['request']
    >({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/auth/candidate/signIn',
          method: 'POST',
          body: args,
        });

        if (result.error) {
          return { error: result.error };
        }

        if (result.data) {
          const user = result.data as IUser;
          if (user.accessToken) {
            const token = jwtDecode(user.accessToken) as {
              roles: string[];
            };
            if (
              token?.roles.find((item) => item.includes(UserRoleEnum.CANDIDATE))
            ) {
              return { data: result.data as IUser };
            }
          }
        }
        const error: FetchBaseQueryError = {
          data: {
            error: true,
            message:
              'You do not have access to the platform. Check with your manager.',
          },
          error: 'incorrect role',
          status: 'CUSTOM_ERROR',
        };

        return { error };
      },
      invalidatesTags: [organizationTag],
    }),
    registerByInvite: builder.mutation<
      registerByInviteMutation['response'],
      registerByInviteMutation['request']
    >({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/auth/manager/register-manager-by-invite-link',
          method: 'POST',
          body: {
            ...args,
            email: args.email.trim().toLowerCase(),
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as IRegisterByInvite };
      },
    }),
    confirmEmail: builder.mutation<
      confirmEmailMutation['response'],
      confirmEmailMutation['request']
    >({
      query: ({ token, password }) => ({
        url: `/auth/confirmEmail?accessToken=${token}`,
        method: 'PATCH',
        body: { password },
      }),
    }),
    resetPassword: builder.mutation<
      resetPasswordMutation['response'],
      resetPasswordMutation['request']
    >({
      query: ({ email }) => ({
        url: `auth/forgotPassword`,
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      }),
    }),
    changePassword: builder.mutation<
      changePasswordMutation['response'],
      changePasswordMutation['request']
    >({
      query: ({ password, accessToken }) => ({
        url: `auth/changePassword`,
        method: 'PUT',
        headers: { authorization: `Bearer ${accessToken}` },
        body: { password, reset: false },
      }),
    }),
    unsecuredLoginCandidate: builder.mutation<
      unsecuredCandidateLogin['response'],
      unsecuredCandidateLogin['request']
    >({
      query: (body) => ({
        url: `auth/document-collection/candidate/sign-up`,
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation<null, void>({
      async queryFn(_args, { getState }, _extraOptions, baseQuery) {
        const state = getState() as RootState;

        await baseQuery({
          url: `/auth/signOut/${state.user.accessToken}`,
          method: 'DELETE',
        });

        resetAllApiState();

        return { data: null };
      },
    }),
  }),
});

export default authService;
